import React, { Children, createContext, isValidElement, ReactElement, ReactNode } from 'react';

export const RadioGroupItemContext = createContext<{ isTabStop: boolean } | undefined>(undefined);

const ARROW_STEP: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

type RadioItemProps = { checked?: boolean; disabled?: boolean };

// WAI-ARIA's radio group: one tab stop (the checked radio, else the first enabled one), and the
// arrow keys move focus to the next or previous enabled radio, wrapping, and check it.
export const RadioGroup: React.FC<{ label: string; className?: string; children: ReactNode }> = ({
  label,
  className,
  children,
}) => {
  const items = Children.toArray(children).filter((child): child is ReactElement<RadioItemProps> =>
    isValidElement(child),
  );
  const checkedIndex = items.findIndex((item) => item.props.checked && !item.props.disabled);
  const tabStop =
    checkedIndex >= 0 ? checkedIndex : items.findIndex((item) => !item.props.disabled);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = ARROW_STEP[event.key];
    // Alt+arrow is the browser's Back/Forward on Windows and Linux.
    if (step === undefined || event.altKey || event.ctrlKey || event.metaKey) return;
    const radios = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        '[role="radio"]:not([aria-disabled="true"])',
      ),
    );
    const current = radios.indexOf(event.target as HTMLElement);
    if (current < 0) return;
    event.preventDefault();
    const next = radios[(current + step + radios.length) % radios.length];
    next.focus();
    next.click();
  };

  return (
    <div role="radiogroup" aria-label={label} className={className} onKeyDown={handleKeyDown}>
      {items.map((item, index) => (
        <RadioGroupItemContext.Provider key={item.key} value={{ isTabStop: index === tabStop }}>
          {item}
        </RadioGroupItemContext.Provider>
      ))}
    </div>
  );
};
